import type { RichTextStyle } from "@library-elements/text";
import { TEXT_STYLES, TEXT_STYLE_TAGS } from "../froala/constants";

/**
 * Typography system (spec §3): exactly five styles. "subtitle" is a legacy
 * alias of "subheading" that older content may still carry — it is accepted
 * on input and always written back as "subheading".
 */
export const TEXT_STYLE_IDS = [
  "display",
  "slide-title",
  "heading",
  "subheading",
  "normal",
] as const;

export type TextStyleId = (typeof TEXT_STYLE_IDS)[number];

export const NORMAL_STYLE: TextStyleId = "normal";

export const isTextStyleId = (value: unknown): value is TextStyleId =>
  typeof value === "string" &&
  (TEXT_STYLE_IDS as readonly string[]).includes(value);

/** Accepts any RichTextStyle (incl. legacy "subtitle") and canonicalizes it. */
export const toTextStyleId = (
  style: RichTextStyle | string | null,
): TextStyleId | null => {
  if (!style) return null;
  if (style === "subtitle") return "subheading";
  return isTextStyleId(style) ? style : null;
};

/** Block tag each style is stored as (h1/h2/h3/h4/p) — shared with Froala. */
export const styleToTag = (style: TextStyleId): string =>
  TEXT_STYLE_TAGS[style];

const TAG_TO_STYLE: Record<string, TextStyleId> = {
  h1: "display",
  h2: "slide-title",
  h3: "heading",
  h4: "subheading",
  p: "normal",
};

export const tagToStyle = (tag: string): TextStyleId | null =>
  TAG_TO_STYLE[tag.toLowerCase()] ?? null;

/** Heading level (1–4) for a style, or null for "normal" (paragraph). */
export const styleToHeadingLevel = (style: TextStyleId): number | null => {
  const tag = styleToTag(style);
  return tag === "p" ? null : Number(tag.slice(1));
};

export const headingLevelToStyle = (level: number): TextStyleId =>
  TAG_TO_STYLE[`h${Math.min(Math.max(level, 1), 4)}`];

/** Class the inline/list-item wrapper carries, e.g. `sd-text-slide-title`. */
/** Controlled wrapper classes (spec §12): sd-display, sd-title, sd-heading, sd-subheading, sd-normal. */
const STYLE_CLASS: Record<TextStyleId, string> = {
  display: "sd-display",
  "slide-title": "sd-title",
  heading: "sd-heading",
  subheading: "sd-subheading",
  normal: "sd-normal",
};

const CLASS_STYLE: Record<string, TextStyleId> = Object.fromEntries(
  (Object.entries(STYLE_CLASS) as Array<[TextStyleId, string]>).map(
    ([style, cls]) => [cls, style],
  ),
);

export const textStyleClass = (style: TextStyleId): string =>
  STYLE_CLASS[style];

export const classToTextStyle = (
  className: string | null,
): TextStyleId | null => {
  if (!className) return null;
  for (const cls of className.split(/\s+/)) {
    if (CLASS_STYLE[cls]) return CLASS_STYLE[cls];
    // Earlier builds on this branch wrote `sd-text-<id>`; still read them.
    const legacy = cls.match(/^sd-text-(.+)$/)?.[1];
    if (legacy) {
      const style = toTextStyleId(legacy);
      if (style) return style;
    }
  }
  return null;
};

/**
 * Design-system CSS variable the style's font-size resolves to. Legacy Froala
 * content stores typography as `font-size: var(--title-size)` inline; the
 * parser maps those variables back to styles through this table.
 */
const SIZE_VAR_TO_STYLE: Record<string, TextStyleId> = {
  "--display-size": "display",
  "--title-size": "slide-title",
  "--heading-size": "heading",
  "--subtitle-size": "subheading",
  "--paragraph-size": "normal",
};

export const styleToSizeVar = (style: TextStyleId): string =>
  `--${TEXT_STYLES[style].size}`;

/**
 * Spec §3.5 — source font size (px) → style. Sizes are rounded first.
 *   ≤16 → normal · 17–20 → subheading · 21–24 → heading · 25–32 → title · ≥33 → display
 */
export const mapFontSizePx = (px: number): TextStyleId => {
  const size = Math.round(px);
  if (size <= 16) return "normal";
  if (size <= 20) return "subheading";
  if (size <= 24) return "heading";
  if (size <= 32) return "slide-title";
  return "display";
};

/**
 * Parse a CSS `font-size` value into a style. Handles px, pt (Word), the
 * design-system `var(--x-size)` form, and `rem` (16px base). Anything else
 * (em, %, keywords) is unknown and yields null so the caller falls back to
 * the block's own style — raw sizes are never kept.
 */
export const fontSizeValueToStyle = (
  value: string | null | undefined,
): TextStyleId | null => {
  if (!value) return null;
  const v = value.trim().toLowerCase();

  const varMatch = v.match(/var\(\s*(--[a-z-]+)\s*[,)]/);
  if (varMatch) return SIZE_VAR_TO_STYLE[varMatch[1]] ?? null;

  const num = Number.parseFloat(v);
  if (Number.isNaN(num)) return null;
  let px: number | null = null;
  if (v.endsWith("px")) px = num;
  else if (v.endsWith("pt")) px = (num * 4) / 3;
  else if (v.endsWith("rem")) px = num * 16;
  if (px == null) return null;
  // On load a raw px is kept as a font_size mark (see schema), not mapped.
  return legacySizeMode ? null : mapFontSizePx(px);
};

// ─── legacy content (existing stories) ──────────────────────────────

let legacySizeMode = false;

/**
 * Run `fn` with raw pixel sizes interpreted the way Froala's toolbar did for
 * EXISTING content: a size is a text style only when it exactly matches the
 * design system's size for that style; anything else is plain text at the
 * block's own size. The §3.5 band table is for pasted content only — applying
 * it on load would restyle stories (e.g. an 18px paragraph becoming a bold
 * Subheading) the moment they are opened.
 */
export const withLegacySizeMapping = <T>(fn: () => T): T => {
  const previous = legacySizeMode;
  legacySizeMode = true;
  try {
    return fn();
  } finally {
    legacySizeMode = previous;
  }
};

export const isLegacySizeMode = (): boolean => legacySizeMode;

/** Pixel value of a CSS font-size (px / pt / rem), or null for anything else. */
export const fontSizeValueToPx = (
  value: string | null | undefined,
): number | null => {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  const num = Number.parseFloat(v);
  if (Number.isNaN(num)) return null;
  if (v.endsWith("px")) return Math.round(num);
  if (v.endsWith("pt")) return Math.round((num * 4) / 3);
  if (v.endsWith("rem")) return Math.round(num * 16);
  return null;
};
